import React from 'react';
import { Modal } from 'react-bootstrap';
import { BSModal } from '~/components/Bootstrap';
import { OpenLR, ProviderPrint } from '~/components/Patient';
import { ReferralType, enumValue, formatDate } from '~/util';
import { ProviderType } from '~/util/providerUtils';

export interface ReferralModalProps {
    close: () => void;
    referralError: string;
    referralSuccess: boolean;
    patient: PatientType;
    provider: ProviderType;
    reason: string;
    notes: string;
    selectedPatient: string;
    setSelectedPatient: (id: string) => void;
    sendReferral: (type: string) => void;
    loading: boolean;
    patientDuplicates: {
        id: number;
        // eslint-disable-next-line babel/camelcase
        first_name: string;
        // eslint-disable-next-line babel/camelcase
        last_name: string;
        dob: string;
        phones: { type: number; number: string }[];
        emails: { type: number; address: string }[];
    }[];
    sentReferral?: ReferralType;
    patientEmail: string;
    location?: undefined | LocationType;
}

export function ReferralModal({
    close,
    referralError,
    referralSuccess,
    patient,
    provider,
    reason,
    notes,
    selectedPatient,
    setSelectedPatient,
    sendReferral,
    loading,
    patientDuplicates,
    sentReferral,
    patientEmail,
    location,
}: ReferralModalProps) {
    const [showCopyConfirm, setShowCopyConfirm] = React.useState(false);

    async function copyAuthCode() {
        await navigator.clipboard.writeText(sentReferral!.referral_message.base36Id);
        setShowCopyConfirm(true);
    }

    // hide copy confirmation after 2 seconds
    React.useEffect(() => {
        if (showCopyConfirm) {
            setTimeout(() => {
                setShowCopyConfirm(false);
            }, 2000);
        }
    }, [showCopyConfirm]);

    return (
        <BSModal
            id="referral-confirmation"
            show={!!referralError || referralSuccess || patientDuplicates.length > 0}
            onHide={close}
            aria-labelledby="referral-confirmation-title"
        >
            <Modal.Header closeButton>
                <Modal.Title id="referral-confirmation-title" role="heading" aria-level={1}>
                    {referralSuccess && 'Referral Sent'}
                    {referralError && 'Referral Failed'}
                    {patientDuplicates.length > 0 && 'Confirm Patient Record'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {referralError === 'disabled_user' && (
                    <p>
                        You cannot complete this action because your LeadingReach user linked to
                        Zephyr is disabled. Please link to an active LeadingReach user or contact
                        your LeadingReach administrator.
                    </p>
                )}
                {referralError === 'invalid_email' && <p>The email address is invalid.</p>}
                {referralError === 'large_file' && (
                    <p>
                        The file(s) you uploaded are too large. Please ensure that your files are
                        less than 50MB total.
                    </p>
                )}
                {referralError === 'missing_roles' && (
                    <p>
                        Apologies, we could not complete this action at this time. Please try again
                        later. If the issue persists, please contact the support team at{' '}
                        <a href="mailto:HelpDesk@leadingreach.com">HelpDesk@leadingreach.com</a> or
                        1-866-656-4410.
                    </p>
                )}
                {referralError === 'generic' && (
                    <p>
                        Apologies, we could not complete this action at this time. Please try again
                        later. If the issue persists, please contact the support team at{' '}
                        <a href="mailto:HelpDesk@wellmed.net">HelpDesk@wellmed.net</a> or
                        1-877-435-7576.
                    </p>
                )}
                {referralError === 'connection' && (
                    <p>
                        The provider you selected is not connected to your LeadingReach network.
                        Please contact{' '}
                        <a href="mailto:PortalSupport@WellMed.net">PortalSupport@WellMed.net</a> to
                        connect this provider.
                    </p>
                )}
                {patientDuplicates.length > 0 && (
                    <div>
                        <p>
                            Zephyr detected a matching patient record in your LeadingReach
                            Organization. Select the correct record below, or click &quot;Add and
                            Send&quot; to create a new one and complete the referral.
                        </p>
                        {patientDuplicates.map((dupe) => (
                            <div className="row align-items-center mb-2" key={dupe.id}>
                                <div className="col-1">
                                    <input
                                        type="radio"
                                        name="patient-dupe"
                                        id={`dupe${dupe.id}`}
                                        value={dupe.id}
                                        checked={`${dupe.id}` === selectedPatient}
                                        onChange={(e) => setSelectedPatient(e.target.value)}
                                    />
                                </div>
                                <div className="col">
                                    <label htmlFor={`dupe${dupe.id}`}>
                                        <div className="fw-bold">
                                            {dupe.first_name} {dupe.last_name}
                                        </div>
                                        <div>DOB: {formatDate(dupe.dob)}</div>
                                        <div>
                                            {
                                                dupe.phones.find(
                                                    (phone) =>
                                                        phone.type ===
                                                        enumValue([
                                                            'core_app',
                                                            'phone_types',
                                                            'Mobile',
                                                        ])
                                                )?.number
                                            }
                                        </div>
                                        <div>
                                            {
                                                dupe.emails.find(
                                                    (email) =>
                                                        email.type ===
                                                        enumValue([
                                                            'core_app',
                                                            'email_types',
                                                            'Personal',
                                                        ])
                                                )?.address
                                            }
                                        </div>
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {referralSuccess && (
                    <>
                        <p>
                            A referral has been sent to{' '}
                            <span className="fw-bold">
                                {provider?.orgName || provider?.practiceName || ''}
                            </span>{' '}
                            regarding{' '}
                            <span className="fw-bold">
                                {patient?.first_name} {patient?.last_name}
                            </span>
                            . To generate a PDF summary for the patient, click{' '}
                            <span className="fw-bold">Patient Printout</span>.
                        </p>
                        <p>
                            The <span className="fw-bold">Referral ID</span> is{' '}
                            <span className="fw-bold">
                                {sentReferral?.referral_message.base36Id}
                            </span>
                            <button
                                title="Copy Referral ID"
                                type="button"
                                className="link-button ms-1 me-2"
                                aria-label="Copy Referral ID"
                                onClick={copyAuthCode}
                            >
                                <i className="fa-solid fa-copy cursor-pointer text-dark" />
                            </button>
                            {showCopyConfirm && <span>Copied!</span>}
                        </p>
                        <div className="text-center my-2">
                            <ProviderPrint
                                referralStatus="confirmed"
                                patient={patient}
                                provider={provider}
                                reason={reason}
                                notes={notes}
                                sentReferral={sentReferral!}
                                patientEmail={patientEmail}
                                location={location}
                            />
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer
                className={
                    patientDuplicates.length > 0 || referralSuccess ? 'justify-content-between' : ''
                }
            >
                {referralSuccess && <OpenLR referralID={sentReferral?.referral_message.base36Id} />}
                {(referralSuccess || referralError) && (
                    <button type="button" className="btn btn-secondary" onClick={close}>
                        Close
                    </button>
                )}
                {patientDuplicates.length > 0 && (
                    <>
                        <div>
                            <button
                                type="button"
                                className="btn btn-secondary justify-con"
                                onClick={close}
                            >
                                Cancel
                            </button>
                        </div>
                        <div>
                            <button
                                type="button"
                                className="btn btn-info me-3"
                                onClick={() => sendReferral('force')}
                                disabled={loading}
                            >
                                Add and Send
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => sendReferral('select')}
                                disabled={!selectedPatient || loading}
                            >
                                Send
                            </button>
                        </div>
                    </>
                )}
            </Modal.Footer>
        </BSModal>
    );
}
